import { useState, useEffect } from 'react';
import { differenceInCalendarDays, format, parseISO, isBefore } from 'date-fns';
import { bookingsApi, exportApi, rewardsApi, ApiRequestError, type Booking, type RewardStatus } from '@/lib/api';
import RewardStatusCard from '@/components/RewardStatusCard';
import BadgeCollectionModal from '@/components/BadgeCollectionModal';

type TabType = 'upcoming' | 'pending' | 'past' | 'cancelled';

export default function MyBookingsPage() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('upcoming');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionData, setSubscriptionData] = useState<{
    token: string | null;
    subscriptionUrl: string | null;
    webcalUrl: string | null;
  } | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [rewardStatus, setRewardStatus] = useState<RewardStatus | null>(null);
  const [rewardLoading, setRewardLoading] = useState(true);
  const [showBadgeModal, setShowBadgeModal] = useState(false);

  const fetchBookings = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { bookings } = await bookingsApi.getMine();
      setBookings(bookings);
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.message);
      } else {
        setError('Failed to load bookings');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchRewardStatus = async () => {
    setRewardLoading(true);
    try {
      const status = await rewardsApi.getStatus();
      setRewardStatus(status);
    } catch (err) {
      console.error('Failed to fetch reward status:', err);
    } finally {
      setRewardLoading(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchRewardStatus();
  }, []);

  const fetchSubscriptionToken = async () => {
    try {
      const data = await exportApi.getSubscriptionToken();
      setSubscriptionData(data);
    } catch (err) {
      console.error('Failed to fetch subscription token:', err);
    }
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcomingBookings = bookings.filter(
    // Include current stays (in-progress) as well as future stays
    (b) => b.status === 'approved' && !isBefore(parseISO(b.end_date), today)
  );

  const pendingBookings = bookings.filter((b) => b.status === 'pending');

  const pastBookings = bookings.filter(
    (b) =>
      b.status === 'approved' && isBefore(parseISO(b.end_date), today)
  );

  const cancelledBookings = bookings.filter((b) => b.status === 'cancelled');

  const currentBookings =
    activeTab === 'upcoming'
      ? upcomingBookings
      : activeTab === 'pending'
      ? pendingBookings
      : activeTab === 'cancelled'
      ? cancelledBookings
      : pastBookings;

  const handleCancel = async (id: string) => {
    const reason = prompt('Please provide a reason for cancelling this booking:');
    if (!reason?.trim()) return;

    setActionLoading(id);
    try {
      await bookingsApi.cancel(id, reason.trim());
      await fetchBookings();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        alert(err.message);
      } else {
        alert('Failed to cancel booking');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const handleExportBooking = async (bookingId: string) => {
    try {
      await exportApi.downloadBookingIcs(bookingId);
    } catch (err) {
      console.error('Failed to export booking:', err);
      alert(err instanceof Error ? err.message : 'Failed to export booking');
    }
  };

  const handleExportAll = async () => {
    setShowExportMenu(false);
    try {
      await exportApi.downloadMyBookingsIcs();
    } catch (err) {
      console.error('Failed to export bookings:', err);
      alert(err instanceof Error ? err.message : 'Failed to export bookings');
    }
  };

  const handleOpenSubscriptionModal = async () => {
    setShowSubscriptionModal(true);
    setShowExportMenu(false);
    await fetchSubscriptionToken();
  };

  const handleCreateSubscription = async () => {
    setSubscriptionLoading(true);
    try {
      const data = await exportApi.createSubscriptionToken();
      setSubscriptionData(data);
    } catch (err) {
      console.error('Failed to create subscription:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleRevokeSubscription = async () => {
    if (!confirm('Revoke calendar subscription? Any synced calendars will stop updating.')) {
      return;
    }
    setSubscriptionLoading(true);
    try {
      await exportApi.revokeSubscriptionToken();
      setSubscriptionData({ token: null, subscriptionUrl: null, webcalUrl: null });
    } catch (err) {
      console.error('Failed to revoke subscription:', err);
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };

  const formatDateRange = (startDate: string, endDate: string) => {
    const start = format(parseISO(startDate), 'MMM d, yyyy');
    const end = format(parseISO(endDate), 'MMM d, yyyy');
    if (start === end) return start;
    return `${start} - ${end}`;
  };

  const getNights = (startDate: string, endDate: string) => {
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    return Math.max(0, differenceInCalendarDays(end, start));
  };

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'upcoming', label: 'Upcoming', count: upcomingBookings.length },
    { key: 'pending', label: 'Pending', count: pendingBookings.length },
    { key: 'past', label: 'Past', count: pastBookings.length },
    { key: 'cancelled', label: 'Cancelled', count: cancelledBookings.length },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">My Bookings</h1>

        {/* Export dropdown */}
        <div className="relative">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            className="btn-secondary"
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Calendar Export
            <svg className="w-4 h-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showExportMenu && (
            <>
              <div
                className="fixed inset-0 z-10"
                onClick={() => setShowExportMenu(false)}
              />
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 z-20 border border-gray-200">
                <button
                  onClick={handleExportAll}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download All (.ics)
                </button>
                <button
                  onClick={handleOpenSubscriptionModal}
                  className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Subscribe to Calendar
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Reward Status Card */}
      {(rewardStatus || rewardLoading) && (
        <RewardStatusCard
          status={rewardStatus!}
          isLoading={rewardLoading}
          onBadgeClick={() => setShowBadgeModal(true)}
        />
      )}

      {/* Badge Collection Modal */}
      {showBadgeModal && rewardStatus && (
        <BadgeCollectionModal
          status={rewardStatus}
          onClose={() => setShowBadgeModal(false)}
        />
      )}

      <div className="card">
        {/* Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-4 px-6 text-sm font-medium border-b-2 ${
                  activeTab === tab.key
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
                {tab.count > 0 && (
                  <span
                    className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.key
                        ? 'bg-primary-100 text-primary-600'
                        : 'bg-gray-100 text-gray-600'
                    }`}
                  >
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading bookings...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">{error}</p>
              <button
                onClick={fetchBookings}
                className="mt-4 text-primary-600 hover:text-primary-700"
              >
                Try again
              </button>
            </div>
          ) : currentBookings.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="mt-4">
                {activeTab === 'upcoming' && 'No upcoming bookings'}
                {activeTab === 'pending' && 'No pending requests'}
                {activeTab === 'past' && 'No past bookings'}
                {activeTab === 'cancelled' && 'No cancelled bookings'}
              </p>
              <p className="text-sm mt-2">
                Request dates from the calendar to book your stay.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {currentBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {formatDateRange(booking.start_date, booking.end_date)}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({getNights(booking.start_date, booking.end_date)} night
                          {getNights(booking.start_date, booking.end_date) !== 1 ? 's' : ''})
                        </span>
                      </div>

                      {/* Status badge */}
                      <div className="mt-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            booking.status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : booking.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-800'
                              : booking.status === 'declined'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {booking.status === 'approved' && 'Confirmed'}
                          {booking.status === 'pending' && 'Pending Approval'}
                          {booking.status === 'declined' && 'Declined'}
                          {booking.status === 'cancelled' && 'Cancelled'}
                        </span>
                      </div>

                      {/* Arrival/departure times */}
                      {booking.start_time && booking.end_time && (
                        <p className="mt-2 text-sm text-gray-500">
                          Arrival: {booking.start_time} / Departure: {booking.end_time}
                        </p>
                      )}

                      {/* User message */}
                      {booking.user_message && (
                        <p className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">Your note:</span>{' '}
                          {booking.user_message}
                        </p>
                      )}

                      {/* Decision message */}
                      {booking.decision_message && (
                        <p className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">Owner response:</span>{' '}
                          {booking.decision_message}
                        </p>
                      )}

                      {/* Cancellation reason */}
                      {booking.cancellation_reason && (
                        <p className="mt-2 text-sm text-gray-600">
                          <span className="font-medium">Cancellation reason:</span>{' '}
                          {booking.cancellation_reason}
                        </p>
                      )}

                      {/* Timestamps */}
                      <p className="mt-2 text-xs text-gray-400">
                        Requested {format(parseISO(booking.created_at), 'MMM d, yyyy')}
                        {booking.decided_at &&
                          ` / ${booking.status === 'approved' ? 'Approved' : booking.status === 'cancelled' ? 'Cancelled' : 'Declined'} ${format(
                            parseISO(booking.decided_at),
                            'MMM d, yyyy'
                          )}`}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {booking.status === 'approved' && (
                        <button
                          onClick={() => handleExportBooking(booking.id)}
                          className="text-sm text-gray-500 hover:text-gray-700"
                          title="Export to calendar"
                        >
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>
                      )}
                      {(booking.status === 'pending' || booking.status === 'approved') && (
                        <button
                          onClick={() => handleCancel(booking.id)}
                          disabled={actionLoading === booking.id}
                          className="btn-secondary text-sm"
                        >
                          {actionLoading === booking.id
                            ? 'Cancelling...'
                            : booking.status === 'pending'
                              ? 'Cancel Request'
                              : 'Cancel Booking'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <>
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowSubscriptionModal(false)}
          />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Calendar Subscription
                </h2>

                <p className="text-sm text-gray-600 mb-4">
                  Subscribe to your bookings in Google Calendar, Apple Calendar, or any
                  calendar app that supports iCal feeds. Your calendar will automatically
                  update when bookings change.
                </p>

                {subscriptionData?.token ? (
                  <div className="space-y-4">
                    <div>
                      <label className="label">Subscription URL</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          readOnly
                          value={subscriptionData.subscriptionUrl || ''}
                          className="input flex-1 text-sm font-mono"
                        />
                        <button
                          onClick={() => handleCopyUrl(subscriptionData.subscriptionUrl!)}
                          className="btn-secondary"
                        >
                          Copy
                        </button>
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <a
                        href={subscriptionData.webcalUrl || '#'}
                        className="btn-primary flex-1 text-center"
                      >
                        Open in Calendar App
                      </a>
                    </div>

                    <div className="pt-4 border-t border-gray-200">
                      <button
                        onClick={handleRevokeSubscription}
                        disabled={subscriptionLoading}
                        className="text-sm text-red-600 hover:text-red-700"
                      >
                        {subscriptionLoading ? 'Revoking...' : 'Revoke subscription'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-gray-500 mb-4">
                      Generate a subscription link to sync your bookings with your calendar.
                    </p>
                    <button
                      onClick={handleCreateSubscription}
                      disabled={subscriptionLoading}
                      className="btn-primary"
                    >
                      {subscriptionLoading ? 'Creating...' : 'Create Subscription'}
                    </button>
                  </div>
                )}
              </div>

              <div className="bg-gray-50 px-6 py-3 flex justify-end rounded-b-lg">
                <button
                  onClick={() => setShowSubscriptionModal(false)}
                  className="btn-secondary"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
